import type { QueryClient } from "@tanstack/react-query";
import type { CubeQueryResponse } from "./useInsightsQueries";

// Analytics can lag behind successful writes. Keep the latest confirmed status
// per query until that query observes it, so older refreshes cannot undo a save.
const pending = new WeakMap<QueryClient, Map<string, Map<string, string>>>();

export function rememberSubmittedActionStatus(
  client: QueryClient,
  guid: string,
  status: string
) {
  let queries = pending.get(client);
  if (!queries) {
    queries = new Map();
    pending.set(client, queries);
  }
  for (const query of client
    .getQueryCache()
    .findAll({ queryKey: ["insights"] })) {
    const key = query.queryKey[1];
    if (
      typeof key !== "string" ||
      !(key.startsWith("farmerActions_") || key.startsWith("farmersByAction_"))
    )
      continue;
    const statuses = queries.get(key) ?? new Map<string, string>();
    statuses.set(guid, status);
    queries.set(key, statuses);
  }
}

export function reconcileSubmittedActionStatuses<T>(
  client: QueryClient,
  key: string,
  response: CubeQueryResponse<T>
): CubeQueryResponse<T> {
  const statuses = pending.get(client)?.get(key);
  if (!statuses?.size) return response;
  return {
    ...response,
    data: response.data.map((row) => {
      const record = row as Record<string, unknown>;
      const guid = record["SubmittedActions.guid"] as string;
      const status = statuses.get(guid);
      if (!status) return row;
      if (record["SubmittedActions.status"] === status) {
        statuses.delete(guid);
        return row;
      }
      return { ...row, "SubmittedActions.status": status };
    }),
  };
}
